import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Users, Shield, LayoutDashboard, Workflow, Menu } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from './Tooltip';
import { useAuth } from '../helpers/useAuth';
import { useSubscription } from '../helpers/useSubscription';
import { useBranding } from '../helpers/useBranding';
import { Button } from './Button';
import { UserSettingsDialog } from './UserSettingsDialog';
import { OrgSettingsDialog } from './OrgSettingsDialog';
import { UserAvatar } from './UserAvatar';
import { ThemeModeSwitch } from './ThemeModeSwitch';
import { LanguageSelector } from './LanguageSelector';
import { useTranslation } from '../helpers/useLocalization';
import { ErrorBoundary } from './ErrorBoundary';
import { SiteThemeLoader } from './SiteThemeLoader';
import styles from './AppLayout.module.css';

interface AppLayoutProps {
  children: React.ReactNode;
  enableSiteTheme?: boolean;
}

export const AppLayout = ({ children, enableSiteTheme = true }: AppLayoutProps) => {
  const location = useLocation();
  const { authState, logout } = useAuth();
  const { data: subscription } = useSubscription();
  const { branding, isWhiteLabeled } = useBranding();
  const { t } = useTranslation();
  const [mobileLogoMenuOpen, setMobileLogoMenuOpen] = useState(false);
  const mobileLogoMenuRef = useRef<HTMLDivElement | null>(null);
  const tier = subscription?.tier || 'free';
  const tierBadgeLabel = tier === 'enterprise' ? 'ENT' : tier === 'pro' ? 'PRO' : 'FREE';

  // Apply branding: favicon, custom CSS, and theme tokens
  useEffect(() => {
    if (branding.faviconUrl && branding.faviconUrl !== '/favicon.png') {
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = branding.faviconUrl;
    }
    if (branding.customCss) {
      let style = document.getElementById('branding-custom-css') as HTMLStyleElement | null;
      if (!style) {
        style = document.createElement('style');
        style.id = 'branding-custom-css';
        document.head.appendChild(style);
      }
      style.textContent = branding.customCss;
      return () => { style?.remove(); };
    }
  }, [branding.faviconUrl, branding.customCss]);

  useEffect(() => {
    if (branding.themeTokens) {
      try {
        const tokens = typeof branding.themeTokens === 'string'
          ? JSON.parse(branding.themeTokens)
          : branding.themeTokens;
        const root = document.documentElement;
        for (const [key, value] of Object.entries(tokens)) {
          if (typeof value === 'string') {
            root.style.setProperty(`--${key}`, value);
          }
        }
        return () => {
          for (const key of Object.keys(tokens)) {
            root.style.removeProperty(`--${key}`);
          }
        };
      } catch { /* ignore invalid JSON */ }
    }
  }, [branding.themeTokens]);

  useEffect(() => {
    setMobileLogoMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!mobileLogoMenuOpen) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (mobileLogoMenuRef.current?.contains(target)) return;
      setMobileLogoMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [mobileLogoMenuOpen]);

  return (
    <div className={styles.layout}>
      {enableSiteTheme && <SiteThemeLoader />}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/dashboard" className={`${styles.brand} ${styles.desktopBrand}`}>
            <img src={branding.logoUrl} alt={branding.brandName} className={styles.logo} />
            <span className={styles.brandName}>
              {isWhiteLabeled ? (
                <span>{branding.brandName}</span>
              ) : (
                <>
                  <span>tasking</span>
                  <span className={styles.techSuffix}>.tech</span>
                </>
              )}
            </span>
          </Link>
          <div className={styles.mobileLogoMenuWrap} ref={mobileLogoMenuRef}>
            <button
              type="button"
              className={styles.mobileLogoButton}
              aria-label="Open navigation menu"
              aria-expanded={mobileLogoMenuOpen}
              onClick={() => setMobileLogoMenuOpen((open) => !open)}
            >
              <Menu size={18} className={styles.mobileHamburgerIcon} />
              <img src={branding.logoUrl} alt={branding.brandName} className={styles.logo} />
              <span className={styles.brandName}>
                {isWhiteLabeled ? (
                  <span>{branding.brandName}</span>
                ) : (
                  <>
                    <span>tasking</span>
                    <span className={styles.techSuffix}>.tech</span>
                  </>
                )}
              </span>
            </button>
            {mobileLogoMenuOpen && (
              <div className={styles.mobileLogoMenu}>
                <Link to="/taskingbot" className={styles.mobileLogoMenuItem}>
                  <img
                    src="/taskingbot.png"
                    alt=""
                    aria-hidden="true"
                    className={styles.mobileLogoMenuItemIcon}
                  />
                  TaskingBot
                </Link>
                <Link to="/dashboard" className={styles.mobileLogoMenuItem}>
                  <LayoutDashboard size={15} className={styles.mobileLogoMenuLucideIcon} />
                  Dashboard
                </Link>
                <Link to="/workflows" className={styles.mobileLogoMenuItem}>
                  <Workflow size={15} className={styles.mobileLogoMenuLucideIcon} />
                  Workflows
                </Link>
                <Link to="/repoforge" className={styles.mobileLogoMenuItem}>
                  <img
                    src="/icons/repoforge-icon.png"
                    alt=""
                    aria-hidden="true"
                    className={styles.mobileLogoMenuItemIcon}
                  />
                  RepoForge
                </Link>
              </div>
            )}
          </div>
          <nav className={styles.nav}>
            {authState.type === 'authenticated' && authState.user.role === 'admin' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/admin/users"
                    className={`${styles.navLink} ${
                      location.pathname.startsWith('/admin') ? styles.active : ''
                    }`}
                  >
                    <Users size={18} />
                    <span className={styles.navLabel}>{t.nav.admin}</span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent>{t.nav.admin}</TooltipContent>
              </Tooltip>
            )}
          </nav>
        </div>
        <div className={styles.headerRight}>
          {authState.type === 'authenticated' && authState.user.email === 'ryand32@gmail.com' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/ttadmin">
                  <Button variant="ghost" size="icon-md" aria-label="Admin Panel">
                    <Shield size={16} style={{ color: '#ef4444' }} />
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent>Admin Panel</TooltipContent>
            </Tooltip>
          )}
          <LanguageSelector compact />
          <ThemeModeSwitch />
          <OrgSettingsDialog />
          <UserSettingsDialog />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={styles.desktopOnly}
                onClick={async () => {
                  await logout();
                  window.location.href = '/';
                }}
              >
                <LogOut size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.auth.signOut}</TooltipContent>
          </Tooltip>
          {authState.type === 'authenticated' && (
            <Link
              to="/pricing"
              className={styles.userProfile}
              aria-label={`Subscription: ${tier}`}
              title={`${authState.user.displayName} • ${tier}`}
            >
              <UserAvatar user={authState.user} size="sm" />
              <span
                className={styles.userTierTiny}
                style={{
                  background:
                    tier === 'enterprise'
                      ? 'linear-gradient(135deg, #8b5cf6, #a78bfa)'
                      : tier === 'pro'
                        ? 'linear-gradient(135deg, #f97316, #fb923c)'
                        : 'linear-gradient(135deg, #22c55e, #4ade80)',
                }}
              >
                {tierBadgeLabel}
              </span>
            </Link>
          )}
        </div>
      </header>
      <main className={styles.main}>
        <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
      </main>
    </div>
  );
};
